/**
 * Sewing Efficiency — the factory headline card: legacy's seven KPI tiles, as one card of six
 * segments.
 *
 * **Every figure comes from the server's `totals` view; nothing here computes a measure.** Overall
 * efficiency is `SUM(earned)/SUM(worked)`, so averaging the visible efficiency column would give a
 * plausible wrong answer (§4.3, §5.2). This file chooses columns from one response — `metric` picks
 * the family, `include_ppa` swaps efficiency alone, `mode` picks the achieved/failed pair.
 *
 * These are report-local segments, not the deferred status components (§4.6): nothing is shared,
 * nothing carries a status label, and no threshold is invented. Which side of its target a figure
 * fell is the only presentation decision, and it compares two server-computed numbers via
 * `target.tsx`. Legacy's 0.60/0.40 efficiency bands stay unported.
 *
 * `design-notes.md` in this folder carries the layout and sparkline rationale.
 */

import { useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faGaugeHigh,
  faLayerGroup,
  faShirt,
  faStopwatch,
  faTableColumns,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Area, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import { format, isValid, parseISO } from 'date-fns';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColor,
  type ChartColorToken,
  type ChartConfig,
} from '@/components/ui/chart';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { decimal, integer } from '@/components/report/groupedColumns';
import { cn } from '@/lib/utils';

import type { ChartMode, Metric } from './filters';
import { STANDING, StandingArrow, standing, standingGap, type Standing } from './target';

/**
 * The one row the `totals` view returns; `report.yaml` in `winsight-server` is authoritative.
 * Efficiency values are 0–1 fractions, scaled for display here. Nullable throughout: null means the
 * window contained no rows, which is not zero, so a card shows an em dash.
 */
export interface SewingTotals {
  lines_total: number | null;

  eins_output: number | null;
  sfts_output: number | null;
  target_eins_output: number | null;
  target_sfts_output: number | null;
  /** Output ÷ target for quantity, server-computed (§4.3). No `_ppa_` sibling: the strip plan
   * denominator does not plan PPA. */
  eins_output_pct: number | null;
  sfts_output_pct: number | null;

  eins_produced_minutes: number | null;
  sfts_produced_minutes: number | null;
  eins_ppa_produced_minutes: number | null;
  sfts_ppa_produced_minutes: number | null;
  eins_average_smv: number | null;
  sfts_average_smv: number | null;

  eins_efficiency: number | null;
  sfts_efficiency: number | null;
  eins_ppa_efficiency: number | null;
  sfts_ppa_efficiency: number | null;
  target_efficiency: number | null;

  eins_output_achieved: number | null;
  eins_output_failed: number | null;
  sfts_output_achieved: number | null;
  sfts_output_failed: number | null;
  eins_efficiency_achieved: number | null;
  eins_efficiency_failed: number | null;
  sfts_efficiency_achieved: number | null;
  sfts_efficiency_failed: number | null;

  eins_sample_lines: number | null;
  sfts_sample_lines: number | null;
  eins_total_styles: number | null;
  sfts_total_styles: number | null;

  changeover_qty: number | null;
  changeover_lines: number | null;
}

/**
 * One row of the `totals-daily` view — the same figures per day. It is `totals` minus the counts
 * whose subqueries carry no date key: at daily grain those replicate rather than vary, so the three
 * cards showing them draw no trend rather than a flat line that looks like data.
 */
export interface SewingDailyTotals {
  tran_date: string | null;
  lines_total: number | null;

  eins_output: number | null;
  sfts_output: number | null;
  target_eins_output: number | null;
  target_sfts_output: number | null;
  eins_output_pct: number | null;
  sfts_output_pct: number | null;

  eins_produced_minutes: number | null;
  sfts_produced_minutes: number | null;
  eins_ppa_produced_minutes: number | null;
  sfts_ppa_produced_minutes: number | null;
  eins_average_smv: number | null;
  sfts_average_smv: number | null;

  eins_efficiency: number | null;
  sfts_efficiency: number | null;
  eins_ppa_efficiency: number | null;
  sfts_ppa_efficiency: number | null;
  target_efficiency: number | null;

  eins_output_achieved: number | null;
  eins_output_failed: number | null;
  sfts_output_achieved: number | null;
  sfts_output_failed: number | null;
  eins_efficiency_achieved: number | null;
  eins_efficiency_failed: number | null;
  sfts_efficiency_achieved: number | null;
  sfts_efficiency_failed: number | null;
}

const POINT_DATE = 'd MMM';
const PERIOD_DATE = 'd MMM yyyy';

function count(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : integer.format(Math.round(value));
}

function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${decimal.format(value * 100)}%`;
}

function smv(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : decimal.format(value);
}

/** Fractions on the wire, percentages on the plot — the same scaling every chart here applies. */
function scaled(value: number | null, isPercent: boolean): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  return isPercent ? value * 100 : value;
}

function readableDate(raw: string | null, pattern: string): string {
  const parsed = parseISO(raw ?? '');
  return isValid(parsed) ? format(parsed, pattern) : (raw ?? '');
}

interface SparkPoint {
  /** For the tooltip heading — the day this point is. */
  label: string;
  actual: number | null;
  target: number | null;
  /** Which side of its target this day fell — worked out once, so fill and figure agree. */
  standing: Standing | null;
}

interface Trend {
  points: readonly SparkPoint[];
  /** Names the series in the accessible name and the tooltip. */
  measure: string;
  format: (value: number) => string;
  /** False when the daily grain carries no target for this measure, as for produced minutes. */
  hasTarget: boolean;
}

/** The series' own colour, and the fill on a day there is nothing to compare against. */
const SERIES_COLOR: ChartColorToken = 'chart-1';

/** How far back the fill is washed. Higher than a plain sparkline needs: it carries a reading. */
const FILL_OPACITY = 0.18;

/**
 * A stretch of the fill that reads as one colour, as a fraction of the drawn span — fractions
 * because these become SVG gradient stops, measured 0–1 across the filled path's bounding box.
 */
interface FillRun {
  from: number;
  to: number;
  token: ChartColorToken;
}

/** The wash a day takes: its side of target, or the series colour where it has no side. */
function fillToken(of: Standing | null): ChartColorToken {
  return of === null ? SERIES_COLOR : STANDING[of].chart;
}

/**
 * Where between two days the fill changes colour — at the crossing of the two plotted series, not
 * at the day boundary, or the colour turns visibly to one side of where the lines meet. A pair with
 * nothing to compare has no crossing and falls back to the midpoint.
 */
function crossing(before: SparkPoint | undefined, after: SparkPoint, index: number): number {
  const midpoint = index - 0.5;
  if (before === undefined) {
    return midpoint;
  }
  const from = standingGap(before.actual, before.target);
  const to = standingGap(after.actual, after.target);
  if (from === null || to === null) {
    return midpoint;
  }
  const share = from / (from - to);
  if (!Number.isFinite(share)) {
    return midpoint;
  }
  return index - 1 + Math.min(1, Math.max(0, share));
}

/**
 * The fill, run by run, left to right — one run when every day fell the same side of its target.
 * Days outside the drawn span clamp to the ends, so an idle first day of the month shifts nothing.
 */
function fillRuns(points: readonly SparkPoint[]): FillRun[] {
  let drawnFrom: number | undefined;
  let drawnTo = 0;
  for (const [index, point] of points.entries()) {
    if (point.actual === null) {
      continue;
    }
    drawnFrom ??= index;
    drawnTo = index;
  }
  if (drawnFrom === undefined) {
    return [];
  }

  const start = drawnFrom;
  const span = drawnTo - start;
  const offset = (position: number): number =>
    span <= 0 ? 0 : Math.min(1, Math.max(0, (position - start) / span));

  const runs: FillRun[] = [];
  let token: ChartColorToken | undefined;
  let from = 0;
  let previous: SparkPoint | undefined;

  for (const [index, point] of points.entries()) {
    const next = fillToken(point.standing);
    if (token !== undefined && next !== token) {
      const boundary = offset(crossing(previous, point, index));
      runs.push({ from, to: boundary, token });
      from = boundary;
    }
    token = next;
    previous = point;
  }
  if (token !== undefined) {
    runs.push({ from, to: 1, token });
  }
  return runs;
}

/**
 * The area sparkline under a segment's headline figure — a shape, not a second reading of it. The
 * y-domain is tight to the data, so the filled height is not proportional to the value and nothing
 * reads a magnitude off it. The fill colour says which side of its target each day fell, via
 * `target.tsx`; it decides nothing and a day with a null or zero target is neutral.
 *
 * `isAnimationActive` is deliberately left unset: Recharts' `"auto"` default reads
 * `prefers-reduced-motion` itself, and passing `true` would opt this card out of §4.9.
 *
 * `design-notes.md` records why one `<Area>` over a computed gradient rather than two, and why
 * `monotone` rather than `natural`.
 */
function CardSparkline({ trend, period }: { trend: Trend; period: string }) {
  // React 19's useId is not a valid SVG fragment id on its own, hence the strip.
  const fillId = `spark-fill-${useId().replace(/[^\w-]/g, '')}`;
  const runs = trend.hasTarget ? fillRuns(trend.points) : [];
  const split = runs.length > 1;
  const config = {
    actual: { label: trend.measure, color: SERIES_COLOR },
    ...(trend.hasTarget ? { target: { label: 'Target', color: 'muted-foreground' } } : {}),
  } satisfies ChartConfig;

  return (
    <ChartContainer
      config={config}
      aria-label={`${trend.measure} by day, ${period}`}
      className="aspect-auto h-10 w-full"
    >
      <ComposedChart data={[...trend.points]} accessibilityLayer margin={{ top: 3, bottom: 1 }}>
        {split ? (
          <defs>
            {/* Paired stops at each boundary, so the colour turns rather than blending. */}
            <linearGradient id={fillId} x1="0" y1="0" x2="1" y2="0">
              {runs.flatMap((run, index) => [
                <stop key={`${index}-from`} offset={run.from} stopColor={chartColor(run.token)} />,
                <stop key={`${index}-to`} offset={run.to} stopColor={chartColor(run.token)} />,
              ])}
            </linearGradient>
          </defs>
        ) : null}
        {/* Hidden, not absent: the tooltip needs the scales to find a day. */}
        <XAxis dataKey="label" hide />
        <YAxis hide domain={['dataMin', 'dataMax']} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelKey="label"
              indicator="line"
              formatter={(value) =>
                typeof value === 'number' ? trend.format(value) : String(value)
              }
            />
          }
        />
        {/* The measured series leads, so the tooltip reads it out first. */}
        <Area
          type="monotone"
          dataKey="actual"
          // Stays the series colour: the fill carries the verdict, the stroke carries the identity
          // the tooltip's swatch names.
          stroke={chartColor(SERIES_COLOR)}
          strokeWidth={2}
          fill={split ? `url(#${fillId})` : chartColor(runs[0]?.token ?? SERIES_COLOR)}
          fillOpacity={FILL_OPACITY}
          dot={false}
          // A day the factory did not run is a gap, not a dip to zero.
          connectNulls={false}
        />
        {trend.hasTarget ? (
          <Line
            type="monotone"
            dataKey="target"
            stroke={chartColor('muted-foreground')}
            strokeWidth={1}
            strokeDasharray="3 3"
            dot={false}
            connectNulls
          />
        ) : null}
      </ComposedChart>
    </ChartContainer>
  );
}

/**
 * The columns one metric contributes, chosen — never combined. Written out per metric rather than
 * built from a prefix, so a renamed column fails to compile and a search finds it.
 */
interface MetricTotals {
  output: number | null;
  targetOutput: number | null;
  outputPct: number | null;
  producedMinutes: number | null;
  averageSmv: number | null;
  efficiency: number | null;
  sampleLines: number | null;
  totalStyles: number | null;
  achieved: number | null;
  failed: number | null;
}

/** Resolve the row down to the figures on screen: three independent choices, as legacy made them. */
function selectTotals(
  totals: SewingTotals,
  metric: Metric,
  mode: ChartMode,
  includePpa: boolean,
): MetricTotals {
  if (metric === 'EINS') {
    return {
      output: totals.eins_output,
      targetOutput: totals.target_eins_output,
      // Not swapped by includePpa: the strip plan does not plan PPA, so no `_ppa_output_pct` exists.
      outputPct: totals.eins_output_pct,
      // Follows the PPA toggle with efficiency: the two tiles must describe the same quantity.
      producedMinutes: includePpa ? totals.eins_ppa_produced_minutes : totals.eins_produced_minutes,
      averageSmv: totals.eins_average_smv,
      efficiency: includePpa ? totals.eins_ppa_efficiency : totals.eins_efficiency,
      sampleLines: totals.eins_sample_lines,
      totalStyles: totals.eins_total_styles,
      achieved: mode === 'output' ? totals.eins_output_achieved : totals.eins_efficiency_achieved,
      failed: mode === 'output' ? totals.eins_output_failed : totals.eins_efficiency_failed,
    };
  }
  return {
    output: totals.sfts_output,
    targetOutput: totals.target_sfts_output,
    outputPct: totals.sfts_output_pct,
    producedMinutes: includePpa ? totals.sfts_ppa_produced_minutes : totals.sfts_produced_minutes,
    averageSmv: totals.sfts_average_smv,
    efficiency: includePpa ? totals.sfts_ppa_efficiency : totals.sfts_efficiency,
    sampleLines: totals.sfts_sample_lines,
    totalStyles: totals.sfts_total_styles,
    achieved: mode === 'output' ? totals.sfts_output_achieved : totals.sfts_efficiency_achieved,
    failed: mode === 'output' ? totals.sfts_output_failed : totals.sfts_efficiency_failed,
  };
}

interface DayTotals {
  output: number | null;
  targetOutput: number | null;
  outputPct: number | null;
  producedMinutes: number | null;
  efficiency: number | null;
}

/** The same three choices over one day of `totals-daily`, so a card and its trend cannot diverge. */
function selectDay(day: SewingDailyTotals, metric: Metric, includePpa: boolean): DayTotals {
  if (metric === 'EINS') {
    return {
      output: day.eins_output,
      targetOutput: day.target_eins_output,
      outputPct: day.eins_output_pct,
      producedMinutes: includePpa ? day.eins_ppa_produced_minutes : day.eins_produced_minutes,
      efficiency: includePpa ? day.eins_ppa_efficiency : day.eins_efficiency,
    };
  }
  return {
    output: day.sfts_output,
    targetOutput: day.target_sfts_output,
    outputPct: day.sfts_output_pct,
    producedMinutes: includePpa ? day.sfts_ppa_produced_minutes : day.sfts_produced_minutes,
    efficiency: includePpa ? day.sfts_ppa_efficiency : day.sfts_efficiency,
  };
}

/** Two points make a trend; one makes a dot, and a period of one day is better left plain. */
const MIN_TREND_POINTS = 2;

function buildTrend(
  daily: readonly SewingDailyTotals[] | null,
  pick: (day: SewingDailyTotals) => { actual: number | null; target: number | null },
  measure: string,
  isPercent: boolean,
): Trend | undefined {
  if (daily === null || daily.length < MIN_TREND_POINTS) {
    return undefined;
  }

  const points = daily.map((day) => {
    // Compared after scaling, on the numbers the plot draws, so the colour matches the curve.
    const { actual, target } = pick(day);
    const value = scaled(actual, isPercent);
    const goal = scaled(target, isPercent);
    return {
      label: readableDate(day.tran_date, POINT_DATE),
      actual: value,
      target: goal,
      standing: standing(value, goal),
    };
  });

  // A series of nothing is not a trend — the card keeps its figure and drops the plot.
  if (points.every((point) => point.actual === null)) {
    return undefined;
  }

  return {
    points,
    measure,
    format: isPercent
      ? (value) => `${decimal.format(value)}%`
      : (value) => integer.format(Math.round(value)),
    hasTarget: points.some((point) => point.target !== null),
  };
}

interface CardSpec {
  label: string;
  icon: IconDefinition;
  value: string;
  /** Rendered under the figure — its target, or the parts it breaks into. */
  detail: React.ReactNode;
  /** Null where there is no target pair to compare, which is a correct answer, not a gap. */
  standing: Standing | null;
  trend: Trend | undefined;
}

/**
 * One line of the Production mix card. No `standing` and no `trend` because none of the three has a
 * target or a daily series — the type says so rather than leaving three `CardSpec` fields null.
 */
interface MixFigure {
  label: string;
  value: string;
  /** A qualifier under the label. Only Changeovers has one — the lines the count spreads across. */
  detail?: string;
}

interface HeadlineSpecs {
  /** The five measures that move day to day, each with its own target and its own shape. */
  cards: CardSpec[];
  /** The three counts that have neither, grouped into the last card. */
  mix: MixFigure[];
}

/**
 * Where Output % meets its target — 1, being 100% once scaled. **Not a threshold** (§5.2): it is
 * the ratio's own definition, and exists only because a sparkline needs a reference series.
 */
const OUTPUT_PARITY = 1;

/**
 * Everything on the row, resolved from one `totals` row. Cards and mix figures come back together
 * so {@link selectTotals} is applied exactly once and the metric choice has one place to live.
 */
function specs(
  totals: SewingTotals,
  daily: readonly SewingDailyTotals[] | null,
  metric: Metric,
  mode: ChartMode,
  includePpa: boolean,
  canCompare: boolean,
): HeadlineSpecs {
  const chosen = selectTotals(totals, metric, mode, includePpa);
  // The Lines card counts a different comparison in each mode, so it names the one it counted.
  const comparison = mode === 'output' ? 'output target' : 'efficiency target';
  const compare = (actual: number | null, target: number | null): Standing | null =>
    canCompare ? standing(actual, target) : null;

  const outputLabel = metric === 'EINS' ? 'Endline passed' : 'Sewing output';
  const efficiencyLabel = includePpa ? 'Efficiency (with PPA)' : 'Efficiency';
  const outputStanding = compare(chosen.output, chosen.targetOutput);
  const efficiencyStanding = compare(chosen.efficiency, totals.target_efficiency);

  const cards: CardSpec[] = [
    {
      label: efficiencyLabel,
      icon: faGaugeHigh,
      value: percent(chosen.efficiency),
      detail: <TargetLine of={efficiencyStanding} target={percent(totals.target_efficiency)} />,
      standing: efficiencyStanding,
      trend: buildTrend(
        daily,
        (day) => ({
          actual: selectDay(day, metric, includePpa).efficiency,
          target: day.target_efficiency,
        }),
        efficiencyLabel,
        true,
      ),
    },
    {
      label: 'Output %',
      icon: faBullseye,
      value: percent(chosen.outputPct),
      // The Output card's verdict, reused rather than re-derived, so the two segments cannot drift.
      detail: <TargetLine of={outputStanding} target={percent(OUTPUT_PARITY)} />,
      standing: outputStanding,
      trend: buildTrend(
        daily,
        (day) => ({
          actual: selectDay(day, metric, includePpa).outputPct,
          // A day with no target returns a null ratio, so its point is a gap and its wash neutral.
          target: OUTPUT_PARITY,
        }),
        'Output %',
        true,
      ),
    },
    {
      label: outputLabel,
      icon: faShirt,
      value: count(chosen.output),
      detail: <TargetLine of={outputStanding} target={count(chosen.targetOutput)} />,
      standing: outputStanding,
      trend: buildTrend(
        daily,
        (day) => {
          const chosenDay = selectDay(day, metric, includePpa);
          return { actual: chosenDay.output, target: chosenDay.targetOutput };
        },
        outputLabel,
        false,
      ),
    },
    {
      label: 'Produced minutes',
      icon: faStopwatch,
      value: count(chosen.producedMinutes),
      detail: <>Average SMV {smv(chosen.averageSmv)}</>,
      // No target for produced minutes anywhere in the contract, so no comparison and no colour.
      standing: null,
      trend: buildTrend(
        daily,
        (day) => ({ actual: selectDay(day, metric, includePpa).producedMinutes, target: null }),
        'Produced minutes',
        false,
      ),
    },
    {
      label: 'Lines',
      icon: faTableColumns,
      value: count(totals.lines_total),
      detail: (
        <span className="flex flex-col gap-0.5">
          <span className="flex items-baseline gap-1">
            {canCompare ? <StandingArrow of="at-or-over" className="size-2.5 self-center" /> : null}
            <span>
              <span className="font-medium text-foreground">{count(chosen.achieved)}</span> at or
              over the {comparison}
            </span>
          </span>
          <span className="flex items-baseline gap-1">
            {canCompare ? <StandingArrow of="under" className="size-2.5 self-center" /> : null}
            <span>
              <span className="font-medium text-foreground">{count(chosen.failed)}</span> under it
            </span>
          </span>
        </span>
      ),
      // A line count has no target; the two halves under it carry their own arrows.
      standing: null,
      trend: buildTrend(
        daily,
        (day) => ({ actual: day.lines_total, target: null }),
        'Lines running',
        false,
      ),
    },
  ];

  const mix: MixFigure[] = [
    { label: 'Lines running samples', value: count(chosen.sampleLines) },
    { label: 'Styles in production', value: count(chosen.totalStyles) },
    {
      label: 'Changeovers',
      value: count(totals.changeover_qty),
      // `changeover_qty` counts switches, not lines; without this the figure reads as a line count.
      detail: `Across ${count(totals.changeover_lines)} lines`,
    },
  ];

  return { cards, mix };
}

/**
 * The line under a headline figure: its target, and which side of it the figure fell. Keeps
 * `muted-foreground` (5.8:1) rather than a status colour — `status-ok` is 3.29:1, enough for the
 * arrow beside the words but not for the words.
 */
function TargetLine({ of, target }: { of: Standing | null; target: string }) {
  if (of === null) {
    return <>Target {target}</>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <StandingArrow of={of} className="size-2.5" />
      {STANDING[of].words} {target}
    </span>
  );
}

export interface SewingTotalsCardsProps {
  totals: SewingTotals | null;
  isLoading: boolean;
  /** The per-day series, or `null` when it has not arrived or was capped — a capped series is a
   * partial shape presented as the whole one, so it is not drawn at all (§4.3). */
  daily: readonly SewingDailyTotals[] | null;
  dailyIsLoading: boolean;
  /** False when `totals` was capped: the cards then show plain targets and colour nothing. */
  canCompare: boolean;
  metric: Metric;
  mode: ChartMode;
  includePpa: boolean;
}

/**
 * One row of six segments, 1 → 2 → 3 → 6, with no sideways overflow at tablet width (§4.9). The
 * dividers are the grid's own background showing through a 1px gap, **not `divide-x`**: `divide-x`
 * borders every child but the first *in source order*, which on a wrapping grid is the wrong child.
 */
const SEGMENT_GRID = 'grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6';

/**
 * One segment of that card. `bg-card` is what leaves only the gap showing and must not be dropped;
 * the full-height flex column is what aligns the five sparkline baselines via `mt-auto`.
 */
const SEGMENT = 'flex flex-col bg-card px-2.5 py-1';

export function SewingTotalsCards({
  totals,
  isLoading,
  daily,
  dailyIsLoading,
  canCompare,
  metric,
  mode,
  includePpa,
}: SewingTotalsCardsProps) {
  if (isLoading) {
    return (
      <Card className="gap-0 overflow-hidden py-0">
        <div className={SEGMENT_GRID}>
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className={SEGMENT}>
              <Skeleton className="h-24 w-full" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (totals === null) {
    return null;
  }

  const { cards, mix } = specs(totals, daily, metric, mode, includePpa, canCompare);

  // Named from the series, not the filters: the accessible name must describe what is plotted.
  const period =
    daily === null || daily.length === 0
      ? ''
      : `${readableDate(daily[0]?.tran_date ?? null, POINT_DATE)} to ${readableDate(
          daily[daily.length - 1]?.tran_date ?? null,
          PERIOD_DATE,
        )}`;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className={SEGMENT_GRID}>
        {cards.map((spec) => (
          <div key={spec.label} className={SEGMENT}>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FontAwesomeIcon icon={spec.icon} className="size-3" aria-hidden />
              {spec.label}
              {spec.standing === null ? null : (
                <StandingArrow of={spec.standing} className="ml-auto size-3" />
              )}
            </span>

            <span className="mt-0.5 block">
              <span
                className={cn(
                  'text-2xl leading-tight font-semibold',
                  spec.standing === null ? undefined : STANDING[spec.standing].figure,
                )}
              >
                {spec.value}
              </span>
            </span>

            <span className="mt-0.5 text-xs text-muted-foreground">{spec.detail}</span>

            {/* mt-auto keeps the plot on the segment floor, so the row's baselines align. */}
            <div className="mt-auto pt-1">
              {spec.trend !== undefined ? (
                <CardSparkline trend={spec.trend} period={period} />
              ) : dailyIsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : null}
            </div>
          </div>
        ))}

        <ProductionMixSegment figures={mix} />
      </div>
    </Card>
  );
}

/**
 * The last segment: three counts with no target and no daily series. Nothing is coloured and
 * nothing carries an arrow — with no target there is no comparison, and inventing a threshold to
 * have something to shade is what §5.2 forbids. A `<dl>` because it is three label/value pairs, and
 * nothing truncates: a clipped label is a figure nobody can name.
 */
function ProductionMixSegment({ figures }: { figures: readonly MixFigure[] }) {
  return (
    <div className={SEGMENT}>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <FontAwesomeIcon icon={faLayerGroup} className="size-3" aria-hidden />
        Production mix
      </span>

      <dl className="mt-0.5 flex flex-1 flex-col divide-y divide-border/60">
        {figures.map((figure) => (
          <div
            key={figure.label}
            className="flex flex-1 items-baseline justify-between gap-2 py-1 first:pt-0 last:pb-0"
          >
            <dt className="min-w-0 text-xs text-muted-foreground">
              {figure.label}
              {figure.detail === undefined ? null : <span className="block">{figure.detail}</span>}
            </dt>
            {/* tabular-nums so three right-aligned figures line up on their digits. */}
            <dd className="shrink-0 text-lg leading-none font-semibold tabular-nums">
              {figure.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
